import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Spinner } from 'react-bootstrap';
import { FaAward, FaCheckCircle, FaClock, FaEye, FaPlay, FaRoute, FaTasks } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../../api/apiClient';
import UserPageHeader from '../../../common/components/UserPageHeader';
import { getAssetUrl } from '../../../utils/apiBase';

const statusStyles = {
  assigned: 'bg-secondary',
  in_progress: 'bg-primary',
  completed: 'bg-success',
  expired: 'bg-danger'
};

const MyLearningJourneys = () => {
  const navigate = useNavigate();
  const [journeys, setJourneys] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.learningJourneys.getMyJourneys();
      setJourneys(res.data?.data || []);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to load learning journeys');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const completedJourneys = journeys.filter((journey) => journey.learner_status === 'completed').length;
  const inProgressJourneys = journeys.filter((journey) => journey.learner_status === 'in_progress').length;

  return (
    <main className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-indigo-50 p-3 sm:p-6 lg:p-8">
      <div className="mx-auto max-w-7xl">
        <UserPageHeader
          iconClass="fas fa-route"
          title="My Learning Journeys"
          subtitle="Follow guided learning paths, track progress, and earn certificates."
          badgeLabel={`${journeys.length} Assigned`}
        />

        {!loading && journeys.length > 0 && (
          <section className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-3" aria-label="Journey summary">
            {[
              { label: 'Assigned Journeys', value: journeys.length, icon: FaRoute, color: 'text-purple-600 bg-purple-50' },
              { label: 'In Progress', value: inProgressJourneys, icon: FaTasks, color: 'text-blue-600 bg-blue-50' },
              { label: 'Completed', value: completedJourneys, icon: FaCheckCircle, color: 'text-emerald-600 bg-emerald-50' }
            ].map(({ label, value, icon: Icon, color }) => (
              <div key={label} className="flex items-center gap-4 rounded-2xl border border-white bg-white/90 p-4 shadow-sm">
                <span className={`flex h-12 w-12 items-center justify-center rounded-xl ${color}`}>
                  <Icon aria-hidden="true" />
                </span>
                <div>
                  <p className="mb-0 text-2xl font-bold text-slate-900">{value}</p>
                  <p className="mb-0 text-sm text-slate-500">{label}</p>
                </div>
              </div>
            ))}
          </section>
        )}

        {loading ? (
          <div className="flex min-h-64 items-center justify-center rounded-3xl border border-purple-100 bg-white/80">
            <Spinner animation="border" variant="primary" role="status">
              <span className="visually-hidden">Loading journeys...</span>
            </Spinner>
          </div>
        ) : journeys.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-purple-200 bg-white/90 px-6 py-16 text-center shadow-sm">
            <span className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-purple-50">
              <FaRoute className="text-3xl text-purple-400" aria-hidden="true" />
            </span>
            <h3 className="mb-2 text-xl font-bold text-slate-800">No journeys assigned</h3>
            <p className="mb-0 text-slate-500">Your assigned learning journeys will appear here.</p>
          </div>
        ) : (
          <section className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3" aria-label="Assigned learning journeys">
          {journeys.map((journey) => {
            const percent = Math.round(journey.progress_percent || 0);
            const status = journey.learner_status || 'assigned';
            return (
              <article
                key={journey.id}
                className="group flex flex-col overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-md transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
              >
                <div className="relative h-44 overflow-hidden bg-gradient-to-br from-indigo-500 via-purple-600 to-pink-500">
                  {journey.thumbnail ? (
                    <img
                      src={getAssetUrl(journey.thumbnail)}
                      alt={journey.name}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <FaRoute className="text-white text-4xl opacity-80" aria-hidden="true" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/55 via-transparent to-transparent" />
                  <div className="absolute bottom-3 left-4 rounded-full bg-white/90 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-purple-700 backdrop-blur">
                    {journey.category || 'Learning Path'}
                  </div>
                  <div className="absolute top-3 right-3 flex gap-2">
                    {status === 'completed' && (
                      <Badge bg="success" className="d-flex align-items-center gap-1">
                        <FaCheckCircle aria-hidden="true" /> Completed
                      </Badge>
                    )}
                    {journey.certificate_issued && (
                      <Badge bg="warning" text="dark" className="d-flex align-items-center gap-1">
                        <FaAward aria-hidden="true" /> Certificate
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="flex flex-grow flex-col p-5">
                  <h3 className="mb-2 text-xl font-bold text-slate-900">{journey.name}</h3>
                  {journey.description && (
                    <p className="mb-3 line-clamp-2 text-sm leading-6 text-slate-500">{journey.description}</p>
                  )}
                  <div className="flex items-center gap-3 text-sm text-gray-500 mb-3">
                    <span className="d-flex align-items-center gap-1">
                      <FaClock aria-hidden="true" /> {journey.estimated_duration || 'Self-paced'}
                    </span>
                    <Badge className={statusStyles[status] || 'bg-secondary'}>{status.replace('_', ' ')}</Badge>
                  </div>
                  <div className="mb-5 mt-auto">
                    <div className="mb-2 flex justify-between text-xs text-slate-600">
                      <span className="font-medium">Journey progress</span>
                      <span className="font-bold text-purple-700">{percent}%</span>
                    </div>
                    <div className="h-2.5 w-full overflow-hidden rounded-full bg-slate-100">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-purple-500 to-indigo-500 transition-all"
                        style={{ width: `${percent}%` }}
                      />
                    </div>
                  </div>
                  <Button
                    bsPrefix="custom-hamburger"
                    className="flex w-full items-center justify-center gap-2 rounded-lg border border-purple-600 bg-white p-2 text-xs font-semibold text-purple-600 shadow-md transition-all duration-200 hover:bg-purple-50 hover:shadow-lg sm:rounded-xl sm:text-sm"
                    onClick={() => navigate(`/user/learning-journeys/${journey.id}`)}
                    aria-label={`${status === 'completed' ? 'View' : 'Continue'} ${journey.name}`}
                  >
                    {status === 'completed'
                      ? <FaEye aria-hidden="true" />
                      : <FaPlay aria-hidden="true" />}
                    {status === 'completed'
                      ? 'View Journey'
                      : status === 'assigned'
                        ? 'Start Journey'
                        : 'Continue Learning'}
                  </Button>
                </div>
              </article>
            );
          })}
          </section>
        )}
      </div>
    </main>
  );
};

export default MyLearningJourneys;
